/** The "split of a whole" cards of the dashboard bands - ADR-0212, package P9.
 *
 * - `ScopeShareCard` (Home): the inventory's scopes as one 100% bar. Its figures and its shares are
 *   `scopeSplit`'s, which states the **server's** `total_kgco2e` and divides it for display only; a
 *   scope the server did not assess keeps its legend row and says so, and has no segment, so an
 *   omission never reads as a scope with nothing in it. A segment opens the scope's figures.
 * - `SiteShareCard` (Scope 1 and Scope 2): the sites, ranked, as the server's organisation tree
 *   states them for the scope. The drawing never adds sites up: the tree's own total for the scope is
 *   the only whole, and a site's share is `lib/format`'s division of its figure by that total.
 * - `CategoryShareCard` (Scope 1): the categories (fuels, refrigerants) as the inventory states them.
 */

import { StackedShare, HBarList } from "../../components/charts";
import type { HBarItem, SharePart } from "../../components/charts";
import { scopeShades } from "../../components/charts/chartTokens";
import type { Inventory } from "../../api/queries";
import type { OrgTree } from "../../api/vendorSummary";
import { EmptyState } from "../../components/ui";
import { share, tonnes } from "../../lib/format";
import { scopeSplit } from "../dashboards/dashboardModel";
import { categoryWords } from "../scopeTable/model";
import { BandCard } from "./BandCard";
import type { BandRead } from "./BandCard";
import { geometryTonnes } from "./bandModel";
import type { ScopeKey } from "./bandModel";

const SCOPE_COLOUR = { "1": "scope1", "2": "scope2", "3": "scope3" } as const;

export function ScopeShareCard({
  read,
  inventory,
  onOpen,
}: {
  read: BandRead;
  inventory: Inventory | undefined;
  /** Opens the figures of a scope; called with `scope-1`, `scope-2` or `scope-3`. */
  onOpen: (key: string) => void;
}) {
  const split = inventory ? scopeSplit(inventory.totals_by_scope, inventory.total_kgco2e) : undefined;
  const parts: SharePart[] = (split?.rows ?? []).map((row) => ({
    key: row.scope,
    label: `Scope ${row.scope}`,
    value: row.assessed ? row.value : 0,
    text: row.assessed && row.text ? `${row.text} t` : "Not assessed",
    ...(row.share ? { share: row.share } : {}),
    colour: SCOPE_COLOUR[row.scope],
    ...(row.assessed && row.value > 0 ? { receipt: `scope-${row.scope}` } : {}),
  }));
  const drawn = parts.some((part) => part.value > 0);
  return (
    <BandCard title="Emissions by scope" unit="tCO2e" reads={[read]} testId="band-scope-share">
      {drawn ? (
        <StackedShare parts={parts} caption="Emissions by scope" onOpenReceipt={onOpen} testId="band-scope-bar" />
      ) : (
        <EmptyState title="No figures in this period" compact />
      )}
    </BandCard>
  );
}

export function CategoryShareCard({
  read,
  inventory,
  scope,
  onOpen,
}: {
  read: BandRead;
  inventory: Inventory | undefined;
  scope: ScopeKey;
  /** Opens the figures of a category; called with the category key. */
  onOpen: (category: string) => void;
}) {
  const card = inventory?.totals_by_scope.find((candidate) => candidate.scope === scope);
  const rows = (inventory?.by_category ?? [])
    .filter((row) => row.scope === scope && Number(row.kgco2e) > 0)
    .sort((a, b) => Number(b.kgco2e) - Number(a.kgco2e));
  const total = card?.assessed ? card.kgco2e : undefined;
  const parts: SharePart[] = rows.map((row, index) => ({
    key: row.category,
    label: categoryWords(row.category),
    value: Math.max(0, Number(row.kgco2e)),
    text: `${tonnes(row.kgco2e)} t`,
    ...(total ? { share: share(row.kgco2e, total) ?? undefined } : {}),
    // Tonnes take their scope's colour; parts of one scope are tints of it (teal is for counts).
    colour: scopeShades(scope)[Math.min(index, 3)] ?? scopeShades(scope)[0],
    receipt: row.category,
  }));
  return (
    <BandCard title={`Scope ${scope} by source`} unit="tCO2e" reads={[read]} testId="band-category-share">
      {parts.length > 0 ? (
        <StackedShare parts={parts} caption={`Scope ${scope} by source`} onOpenReceipt={onOpen} testId="band-category-bar" />
      ) : (
        <EmptyState title="No figures in this period" compact />
      )}
    </BandCard>
  );
}

export function SiteShareCard({
  read,
  tree,
  scope,
  onOpen,
  more,
}: {
  read: BandRead;
  tree: OrgTree | undefined;
  scope: ScopeKey;
  /** Opens the figures of a site; called with the site's key (`site-<id>`). */
  onOpen: (key: string) => void;
  /** One quiet link to the full list (Facilities), built by the page. */
  more?: React.ReactNode;
}) {
  const at = Number(scope) - 1;
  const sites = (tree?.facilities ?? [])
    .map((site) => ({ site, cell: site.totals.by_scope[at] }))
    .filter((row) => row.cell !== undefined && Number(row.cell.kgco2e) > 0)
    .sort((a, b) => Number(b.cell?.kgco2e) - Number(a.cell?.kgco2e));
  const whole = tree?.workspace.by_scope[at]?.kgco2e;
  const items: HBarItem[] = sites.map(({ site, cell }) => ({
    key: site.facility_id,
    label: site.name,
    value: geometryTonnes(cell?.kgco2e ?? "0"),
    text: `${tonnes(cell?.kgco2e ?? "0")} t`,
    ...(whole && cell ? { note: share(cell.kgco2e, whole) ?? undefined } : {}),
    receipt: `site-${site.facility_id}`,
  }));
  return (
    <BandCard
      title={`Scope ${scope} by site`}
      unit="tCO2e"
      reads={[read]}
      {...(more ? { action: more } : {})}
      testId="band-site-share"
    >
      {items.length > 0 ? (
        <HBarList
          items={items}
          caption={`Scope ${scope} by site`}
          unit="tCO2e"
          colour={SCOPE_COLOUR[scope]}
          onOpenReceipt={onOpen}
          testId="band-site-list"
        />
      ) : (
        <EmptyState title="No site figures in this period" compact />
      )}
    </BandCard>
  );
}
